import './sentry';
import { useApolloClient } from '@apollo/client';
import { GraphiQL } from 'graphiql';
import { DocumentNode, buildSchema, getOperationAST, parse } from 'graphql';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { API_URL, ENV, isProduction } from '@sorare/core/src/config';
import { DeviceFingerPrintProvider as DeviceFingerprintProvider } from '@sorare/core/src/contexts/deviceFingerprint/Provider';
import { StaticLocaleGraphqlProvider } from '@sorare/core/src/contexts/graphql/Provider';
import { SentryProvider } from '@sorare/core/src/contexts/sentry/Provider';
import { SnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { client } from '@sorare/core/src/lib/http';

import './main.css';
import 'graphiql/style.css';

// This avoids errors like "Ambiguous GraphQL document: contains X operations" when there
// are multiple named queries in the playground and we select one when clicking the "play" button
// by filtering other non-selected queries
function filterSelectedDefinitions(
  operationName: string | null | undefined,
  parsedDocument: DocumentNode
) {
  if (!operationName) {
    return parsedDocument;
  }

  const selectedOperationDefinitions = parsedDocument.definitions.filter(
    definition => {
      return (
        definition.kind === 'OperationDefinition' &&
        definition.name?.value === operationName
      );
    }
  );

  const nonOperationDefinitions = parsedDocument.definitions.filter(
    definition => {
      return definition.kind !== 'OperationDefinition';
    }
  );

  return {
    ...parsedDocument,
    definitions: [...selectedOperationDefinitions, ...nonOperationDefinitions],
  };
}

const Playground = () => {
  const apolloClient = useApolloClient();
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    const fetchSchema = async () => {
      const { data } = await client.get(`${API_URL}/schema`);
      setSchema(data);
    };

    fetchSchema();
  }, []);

  const parsedSchema = schema && buildSchema(schema);

  return (
    <GraphiQL
      schema={parsedSchema}
      fetcher={async (graphQLParams, opts) => {
        const { operationName } = graphQLParams;
        const parsedDocument = filterSelectedDefinitions(
          operationName,
          parse(graphQLParams.query)
        );
        const operationNode = getOperationAST(parsedDocument, operationName);
        const context = {
          headers: opts?.headers,
        };

        if (operationNode?.operation === 'subscription') {
          // - `client.subscribe` returns a `Observable<FetchResult>` where `Observable` and
          // `FetchResult` are defined in `@apollo/client`
          // - `fetcher` returns a `Observable<ExecutionResult>` where `Observable` and
          // `ExecutionResult` are defined in `graphql-ws`
          // - They use the same API so `as any` works, ideally we type it but it's a pain to
          // do the conversion
          return apolloClient.subscribe({
            query: parsedDocument,
            variables: graphQLParams.variables,
            context,
          }) as any;
        }

        // Never use the cache so that the "play" button always plays the query
        // except for IntrospectionQuery or else it triggers forever
        apolloClient.defaultOptions = {
          query: {
            fetchPolicy:
              graphQLParams.operationName === 'IntrospectionQuery'
                ? 'cache-first'
                : 'network-only',
          },
        };

        try {
          const resp = await (operationNode?.operation === 'mutation'
            ? apolloClient.mutate({
                mutation: parsedDocument,
                variables: graphQLParams.variables,
                context,
              })
            : apolloClient.query({
                query: parsedDocument,
                variables: graphQLParams.variables,
                context,
              }));

          // Filter out __typename fields in response
          const omitTypename = (key: string, value: unknown) =>
            key === '__typename' ? undefined : value;
          resp.data = JSON.parse(JSON.stringify(resp.data), omitTypename);

          return {
            data: resp.data,
            errors: resp.errors,
          };
        } catch (err: any) {
          // eslint-disable-next-line no-console
          console.error(err);

          if (err.clientErrors?.length > 0) {
            return { errors: err.clientErrors };
          }
          if (err.graphQLErrors?.length > 0) {
            return { errors: err.graphQLErrors };
          }
          if (err.networkError) {
            return { errors: [{ message: err.networkError.message }] };
          }
          return { error: err.message };
        }
      }}
    >
      <GraphiQL.Logo>
        Sorare
        {!isProduction && <span style={{ color: '#32CD32' }}> {ENV}</span>}
      </GraphiQL.Logo>
    </GraphiQL>
  );
};

const showNotification = () => {};

const App = () => {
  return (
    <SentryProvider>
      <SnackNotificationContext.Provider
        value={{
          showNotification,
          clearNotification: () => {},
          notification: null,
        }}
      >
        <DeviceFingerprintProvider>
          <StaticLocaleGraphqlProvider
            disableGraphQLErrorsReport
            locale="en-US"
          >
            <Playground />
          </StaticLocaleGraphqlProvider>
        </DeviceFingerprintProvider>
      </SnackNotificationContext.Provider>
    </SentryProvider>
  );
};

const root = createRoot(document.getElementById('root')!);

root.render(<App />);
